import { ThemeEvents } from "./events.js";

/**
 * Authoritative client-side cart state (ACQ-210).
 *
 * Extracted verbatim from `pyp-gift-manager.js` so the cart cache no longer lives
 * inside a gift feature module. `pyp-gift-manager.js` re-exports `getCart` /
 * `primeCartCache` from here, so its existing importers change zero lines.
 *
 * How the cart stays coherent (ACQ-210):
 * - Short-lived in-memory cache + in-flight dedupe. TTL is generous because every cart
 *   change drops the cache via the `cart:update` subscription below.
 * - The `cart:update` subscription is INVALIDATE-ONLY. We never prime from the event
 *   detail (half the dispatchers emit an empty `{}`); priming is done at the mutation
 *   call site from the response.
 * - Mutation contract: a `/cart/change.js` or `/cart/update.js` response IS the full cart,
 *   so call sites `primeCartCache(response)`. `/cart/add.js` returns line items, not a full
 *   cart, so it INVALIDATES — never prime a non-cart. `/cart/clear.js` does return the
 *   emptied cart, but its flows INVALIDATE too (the cleared cart is trivially re-read).
 *   Any component that dispatches `cart:update`/`CartAddEvent` after a mutation
 *   is kept coherent by the invalidate above (priming there would be undone immediately);
 *   priming pays off where the mutating flow does NOT dispatch before its next read
 *   (the custom-box raw-fetch flows, cart-note, cart-discount).
 * - Post-D there is a single externalized instance (importmap-resolved `@theme/cart-state`),
 *   so import this module via `@theme/*`, never a relative path, or you fork the cache.
 *
 * @typedef {Object} CartCacheOptions
 * @property {boolean} [force=false] - Whether to bypass any cached response
 *
 * @typedef {Object} ShopifyCart
 * @property {Array<any>} items
 * @property {number} item_count
 */

/**
 * Baseline TTL. Was 1500ms while reads were the only freshness guard; with
 * invalidate-on-mutation backing it, a longer window collapses read-read redundancy
 * (multiple components reading on the same page with no mutation between) without ever
 * serving a cart that changed. Tunable single point — Phase C sizes its win against this.
 */
const CART_CACHE_TTL = 10000;

/** @type {ShopifyCart | null} */
let cachedCart = null;
let cacheTimestamp = 0;

// Monotonic guard: bumped on every invalidate so a `/cart.js` fetch already in
// flight when `cart:update` fires cannot re-prime the cache with pre-mutation data
// after it resolves (prime-ordering guard, applied to the read path).
let cacheVersion = 0;

// Highest write-queue sequence whose response has primed the cache (write-path prime guard).
// Two overlapping mutations whose responses resolve out of order would otherwise prime
// stale-over-fresh; wall-clock time can't detect issue-order inversion, so the queue
// stamps each write with a monotonic sequence and `primeCartCache` rejects an older one.
let lastPrimedSequence = 0;

/** @type {Promise<ShopifyCart> | null} */
let inflightCartPromise = null;

// Cache version at the moment the in-flight fetch started. A forced read may share
// the in-flight fetch ONLY while this still equals `cacheVersion` — i.e. no invalidate
// has landed since that request left, so it is already fetching post-mutation data.
let inflightVersion = -1;

function isCacheFresh() {
  return Boolean(cachedCart) && Date.now() - cacheTimestamp < CART_CACHE_TTL;
}

/**
 * Drops the cached cart so the next non-forced read re-fetches. Called on every
 * `cart:update` (invalidate-only) and usable directly by mutation sites.
 */
export function invalidateCartCache() {
  cachedCart = null;
  cacheTimestamp = 0;
  cacheVersion += 1;
}

/**
 * Stores a cart snapshot for fast reuse between components.
 *
 * Monotonic guard: when a caller supplies its write-queue `sequence`
 * (`cartOperationQueue.currentSequence`), a prime older by sequence than one already
 * applied is rejected — this is what keeps priming in write-queue completion order even
 * if two mutation responses resolve out of order. Calls without a sequence (forced reads,
 * legacy callers) carry no ordering claim and are always applied; the read path's own
 * `cacheVersion` guard already protects those against invalidate races.
 *
 * Returns whether the cart was applied, so the cart store (which mirrors this guard
 * and is primed through `storefront-stores.js`) can keep its own state coherent
 * with the cache when the two disagree.
 *
 * @param {ShopifyCart | null | undefined} cart
 * @param {number} [sequence] - Write-queue sequence from `cartOperationQueue.currentSequence`.
 * @returns {boolean} `true` when the cache now holds `cart`.
 */
export function primeCartCache(cart, sequence) {
  // Shape guard: only a full-cart payload may prime. Rejects error-shaped 2xx JSON
  // and /cart/add.js bodies (line items but no item_count) so a bad response can
  // never poison the shared cache — this enforces the "never prime a non-cart"
  // contract for every prime site instead of relying on per-caller checks.
  if (!cart || !Array.isArray(cart.items) || typeof cart.item_count !== "number")
    return false;
  if (typeof sequence === "number") {
    if (sequence < lastPrimedSequence) return false;
    lastPrimedSequence = sequence;
  }
  cachedCart = cart;
  cacheTimestamp = Date.now();
  return true;
}

/**
 * Fetches /cart.js with in-flight deduping and a short-lived cache.
 * @param {CartCacheOptions} [options]
 * @returns {Promise<ShopifyCart>}
 */
export async function getCart(options = {}) {
  const { force = false } = options;

  if (!force && isCacheFresh() && cachedCart) {
    return cachedCart;
  }

  // For non-forced calls, reuse in-flight fetches to prevent duplicate requests
  if (!force && inflightCartPromise) {
    return inflightCartPromise;
  }

  // Forced calls: share the in-flight fetch when it started after the latest
  // invalidate (same `cacheVersion`) — it is already fetching post-mutation data, so
  // concurrent forced readers (e.g. the post-add sticky footer + gift drawer + store
  // getter) collapse onto one /cart.js request instead of each firing their own.
  // If the in-flight fetch predates an invalidate, wait it out and RE-CHECK: another
  // forced caller may have started a fresh (shareable) fetch while we waited.
  // Without the loop, every waiter used to start its own fetch on wake-up, which is
  // exactly the same-millisecond duplicate-request stampede this guards against.
  while (force && inflightCartPromise) {
    if (inflightVersion === cacheVersion) {
      return inflightCartPromise;
    }
    await inflightCartPromise.catch(() => {});
  }

  // Snapshot the cache version before the round trip. If an invalidate (e.g. a
  // `cart:update` from a mutation) lands while this fetch is in flight, the version
  // changes and we drop the now-stale response instead of re-priming the cache.
  const requestVersion = cacheVersion;
  inflightVersion = requestVersion;

  // Add timestamp to bust any CDN/browser/service-worker caching. This defeats the
  // CDN/edge cache but NOT Shopify read-replica lag — post-mutation
  // forced reads must still keep their stale-detection.
  const cacheBuster = `_cb=${Date.now()}`;
  inflightCartPromise = fetch(`/cart.js?${cacheBuster}`, {
    credentials: "same-origin",
    headers: {
      "Cache-Control": "no-cache, no-store, must-revalidate",
      Pragma: "no-cache",
    },
  })
    .then((response) => {
      if (!response.ok) throw new Error("Failed to fetch cart");
      return response.json();
    })
    .then((cart) => {
      // Only prime if no invalidate happened mid-flight (stale-prime guard).
      if (requestVersion === cacheVersion) {
        primeCartCache(cart);
      }
      return cart;
    })
    .catch((error) => {
      console.error("[Cart Cache] Error fetching cart:", error);
      throw error;
    })
    .finally(() => {
      inflightCartPromise = null;
    });

  return inflightCartPromise;
}

// Invalidate-only coherence signal. Never prime from the event detail.
if (typeof document !== "undefined") {
  document.addEventListener(ThemeEvents.cartUpdate, () => {
    invalidateCartCache();
  });
}
